import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle2, XCircle, Loader2, Unplug, X, Copy, Check, Send } from 'lucide-react';
import { apiClient } from '../api/client';
import cloudflareIcon from '../assets/icons/cloudflare.svg';

export default function CloudflareIntegration() {
  const [status, setStatus] = useState<{ connected: boolean; status: string; account_id?: string; webhook_url?: string; webhook_secret?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; zone_count?: number; error?: string } | null>(null);
  const [error, setError] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [accountId, setAccountId] = useState('');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [copiedSecret, setCopiedSecret] = useState(false);

  const loadStatus = async () => {
    try {
      const s = await apiClient.getCloudflareStatus();
      setStatus(s);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { loadStatus(); }, []);

  const handleConnect = async () => {
    if (!apiToken || !accountId) return;
    setConnecting(true);
    setError('');
    try {
      await apiClient.connectCloudflare(apiToken, accountId);
      setApiToken('');
      setAccountId('');
      setToast({ type: 'success', message: 'Cloudflare connected successfully.' });
      await loadStatus();
    } catch (err: any) {
      setError(err?.response?.data || err?.message || 'Failed to connect');
    } finally { setConnecting(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Cloudflare? Workflow actions using Cloudflare will stop working.')) return;
    setDisconnecting(true);
    try {
      await apiClient.disconnectCloudflare();
      setStatus({ connected: false, status: 'disconnected' });
      setToast({ type: 'success', message: 'Cloudflare disconnected.' });
    } catch { /* ignore */ } finally { setDisconnecting(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await apiClient.testCloudflareConnection();
      setTestResult(result);
    } catch (err: any) {
      setTestResult({ success: false, error: err?.message || 'Test failed' });
    } finally { setTesting(false); }
  };

  const handleCopySecret = async () => {
    if (!status?.webhook_secret) return;
    await navigator.clipboard.writeText(status.webhook_secret);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {toast && (
        <Alert className={toast.type === 'error' ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950' : 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950'}>
          <div className="flex items-center justify-between">
            <AlertDescription className={toast.type === 'error' ? 'text-red-900 dark:text-red-100' : 'text-green-900 dark:text-green-100'}>
              {toast.message}
            </AlertDescription>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setToast(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </Alert>
      )}

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <img src={cloudflareIcon} alt="Cloudflare" className="h-8 w-8 object-contain" />
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  Cloudflare
                  {status?.connected && (
                    <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                  )}
                </h1>
                <p className="text-sm text-muted-foreground">DNS, security, cache, Workers, and edge infrastructure management</p>
              </div>
            </div>
            {!status?.connected && (
              <Badge variant="secondary">Not Connected</Badge>
            )}
          </div>

          {status?.connected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                <div className="flex-1">
                  <p className="text-sm font-medium">Account ID</p>
                  <p className="text-xs text-muted-foreground font-mono">{status.account_id}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={handleTest} disabled={testing}>
                  {testing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />}
                  Test Connection
                </Button>
                <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={disconnecting}>
                  {disconnecting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Unplug className="w-3 h-3 mr-1" />}
                  Disconnect
                </Button>
              </div>

              {testResult && (
                <Alert variant={testResult.success ? 'default' : 'destructive'}>
                  <AlertDescription>
                    {testResult.success
                      ? `Connection successful${testResult.zone_count != null ? ` — ${testResult.zone_count} zone${testResult.zone_count !== 1 ? 's' : ''} found` : ''}.`
                      : `Connection failed: ${testResult.error}`}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Connect your Cloudflare account to manage DNS records, purge caches, deploy Workers, and respond to edge incidents from workflows.
              </p>
              <div>
                <Label className="text-sm font-medium">API Token</Label>
                <Input
                  className="mt-1"
                  type="password"
                  placeholder="CF API token with appropriate permissions"
                  value={apiToken}
                  onChange={e => setApiToken(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Generate a token in Cloudflare: My Profile &rarr; API Tokens &rarr; Create Token &rarr; Custom Token
                </p>
                <div className="mt-3 rounded-lg border bg-muted/50 p-3">
                  <p className="text-xs font-medium mb-2">Required token permissions</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                    <div className="text-xs text-foreground flex items-center gap-1.5">
                      <span className="font-mono bg-background border px-1.5 py-0.5 rounded">Zone &rarr; DNS</span>
                      <span className="text-muted-foreground">Edit</span>
                    </div>
                    <div className="text-xs text-foreground flex items-center gap-1.5">
                      <span className="font-mono bg-background border px-1.5 py-0.5 rounded">Zone &rarr; Zone</span>
                      <span className="text-muted-foreground">Read</span>
                    </div>
                    <div className="text-xs text-foreground flex items-center gap-1.5">
                      <span className="font-mono bg-background border px-1.5 py-0.5 rounded">Zone &rarr; Firewall Services</span>
                      <span className="text-muted-foreground">Edit</span>
                    </div>
                    <div className="text-xs text-foreground flex items-center gap-1.5">
                      <span className="font-mono bg-background border px-1.5 py-0.5 rounded">Zone &rarr; Cache Purge</span>
                      <span className="text-muted-foreground">Purge</span>
                    </div>
                    <div className="text-xs text-foreground flex items-center gap-1.5">
                      <span className="font-mono bg-background border px-1.5 py-0.5 rounded">Zone &rarr; Page Rules</span>
                      <span className="text-muted-foreground">Edit</span>
                    </div>
                    <div className="text-xs text-foreground flex items-center gap-1.5">
                      <span className="font-mono bg-background border px-1.5 py-0.5 rounded">Zone &rarr; Zone Settings</span>
                      <span className="text-muted-foreground">Edit</span>
                    </div>
                    <div className="text-xs text-foreground flex items-center gap-1.5">
                      <span className="font-mono bg-background border px-1.5 py-0.5 rounded">Zone &rarr; WAF</span>
                      <span className="text-muted-foreground">Edit</span>
                    </div>
                    <div className="text-xs text-foreground flex items-center gap-1.5">
                      <span className="font-mono bg-background border px-1.5 py-0.5 rounded">Account &rarr; Workers Scripts</span>
                      <span className="text-muted-foreground">Edit</span>
                    </div>
                    <div className="text-xs text-foreground flex items-center gap-1.5">
                      <span className="font-mono bg-background border px-1.5 py-0.5 rounded">Account &rarr; Load Balancing: Monitors and Pools</span>
                      <span className="text-muted-foreground">Edit</span>
                    </div>
                    <div className="text-xs text-foreground flex items-center gap-1.5">
                      <span className="font-mono bg-background border px-1.5 py-0.5 rounded">Account &rarr; Cloudflare Tunnel</span>
                      <span className="text-muted-foreground">Edit</span>
                    </div>
                    <div className="text-xs text-foreground flex items-center gap-1.5">
                      <span className="font-mono bg-background border px-1.5 py-0.5 rounded">Account &rarr; Access: Apps</span>
                      <span className="text-muted-foreground">Edit</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2.5">
                    Set Account Resources to your account and Zone Resources to all zones (or specific zones).
                  </p>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Account ID</Label>
                <Input
                  className="mt-1"
                  type="text"
                  placeholder="e.g. 1a2b3c4d5e6f..."
                  value={accountId}
                  onChange={e => setAccountId(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Found in your dashboard URL: dash.cloudflare.com/<span className="font-mono">[account_id]</span>/home
                </p>
              </div>
              {error && (
                <Alert variant="destructive">
                  <XCircle className="w-4 h-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <Button onClick={handleConnect} disabled={connecting || !apiToken || !accountId}>
                {connecting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Connect Cloudflare
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {status?.connected && status.webhook_url && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-base font-semibold">Webhook Setup</h3>
              {(status as any).webhook_event_count > 0 ? (
                <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">
                  {((status as any).webhook_event_count || 0).toLocaleString()} event{(status as any).webhook_event_count !== 1 ? 's' : ''} received
                </Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground text-[10px] font-normal">No events yet</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              To use Cloudflare triggers (Security Event, Health Check Failure, Worker Failure, SSL Expiring), configure Cloudflare Notifications to send events to Kestrel.
            </p>

            <div className="bg-muted/50 p-4 rounded-lg mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Webhook URL</p>
              <div className="flex items-center gap-2">
                <code className="bg-background px-2.5 py-1.5 rounded text-sm border flex-1 truncate select-all">{status.webhook_url}</code>
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(status.webhook_url || ''); }}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {status.webhook_secret && (
              <div className="bg-muted/50 p-4 rounded-lg mb-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">Webhook Secret</p>
                <div className="flex items-center gap-2">
                  <code className="bg-background px-2.5 py-1.5 rounded text-sm border flex-1 truncate select-all">{status.webhook_secret}</code>
                  <Button variant="outline" size="sm" onClick={handleCopySecret}>
                    {copiedSecret ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </div>
            )}

            <ol className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">1</span>
                <span>In Cloudflare, go to <strong className="text-foreground">Manage account &rarr; Notifications &rarr; Destinations</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">2</span>
                <span>In the <strong className="text-foreground">Webhooks</strong> card, click <strong className="text-foreground">Create</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">3</span>
                <span>Enter a name (e.g. "Kestrel"), paste the <strong className="text-foreground">Webhook URL</strong> above, and paste the <strong className="text-foreground">Webhook Secret</strong> into the Secret field</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">4</span>
                <span>Click <strong className="text-foreground">Save and Test</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">5</span>
                <span>Go to <strong className="text-foreground">All Notifications &rarr; Add</strong>, select an alert type, and choose the webhook as destination</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">6</span>
                <span>Supported triggers: <strong className="text-foreground">HTTP DDoS Attack</strong>, <strong className="text-foreground">WAF Attack</strong>, <strong className="text-foreground">Firewall Events</strong>, <strong className="text-foreground">Health Check Status</strong>, <strong className="text-foreground">Workers Event</strong>, <strong className="text-foreground">SSL Certificate Expiration</strong></span>
              </li>
            </ol>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
